import { createRoute, useSearch, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useCartStore } from '../store';
import { ShoppingCart, Plus, ArrowRight, Smartphone, CreditCard, Settings, Search, Tag, CheckCircle2, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { success?: string; transactionId?: string } => {
    return {
      success: search.success as string | undefined,
      transactionId: search.transactionId as string | undefined,
    };
  },
  component: Storefront,
});

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  category?: string;
  tags?: string[];
  vendorId?: string;
  vendorName?: string;
  isArchived?: boolean;
  stock?: number;
  maxOrderQuantity?: number;
  dimensions?: string;
  details?: string;
}

const MOCK_PRODUCTS = [
  {
    id: 'prod_1',
    name: 'Minimalist Ceramic Vase',
    description: 'A beautifully crafted ceramic vase with a matte finish, perfect for modern interiors.',
    price: 4500,
    imageUrl: 'https://images.unsplash.com/photo-1578500494198-246f612d3b3d?q=80&w=2070&auto=format&fit=crop',
    category: 'Home Decor',
    tags: ['home', 'decor', 'minimalist'],
  },
  {
    id: 'prod_2',
    name: 'Linen Throw Blanket',
    description: 'Soft, breathable 100% linen throw blanket. Adds texture and warmth to any living space.',
    price: 8900,
    imageUrl: 'https://images.unsplash.com/photo-1580828369019-2238b6d77406?q=80&w=2070&auto=format&fit=crop',
    category: 'Textiles',
    tags: ['home', 'textiles', 'cozy'],
  },
  {
    id: 'prod_3',
    name: 'Artisan Coffee Dripper',
    description: 'Hand-poured ceramic coffee dripper for the perfect slow-brew experience.',
    price: 3200,
    imageUrl: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?q=80&w=2021&auto=format&fit=crop',
    category: 'Kitchen',
    tags: ['kitchen', 'coffee', 'artisan'],
  },
  {
    id: 'prod_4',
    name: 'Organic Cotton Towel Set',
    description: 'Plush, highly absorbent towel set made from sustainably sourced organic cotton.',
    price: 6500,
    imageUrl: 'https://images.unsplash.com/photo-1616627561839-074385245bf6?q=80&w=2070&auto=format&fit=crop',
    category: 'Bath',
    tags: ['bath', 'organic', 'essentials'],
  },
];

interface StoreData {
  uid: string;
  storeName: string;
  storeDescription: string;
  city: string;
  profilePicture?: string;
  bannerImage?: string;
  bannerType?: string;
  themeColors?: string[];
}

function Storefront() {
  const { success, transactionId } = Route.useSearch();
  const [products, setProducts] = useState<Product[]>([]);
  const [stores, setStores] = useState<StoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<string>('newest');
  const [showSuccess, setShowSuccess] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  const clearCart = useCartStore((state) => state.clearCart);

  useEffect(() => {
    if (success === 'true') {
      clearCart();
      setShowSuccess(true);
      // Remove query params from URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, [success, clearCart]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        let productsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Product[];

        const storeSnapshot = await getDocs(collection(db, 'stores'));
        const storesData = storeSnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() })) as StoreData[];

        // Seed mock products if empty
        if (productsData.length === 0) {
          console.log('Seeding mock products via API...');
          try {
            const origin = window.location.origin;
            const seedRes = await fetch(`${origin}/api/seed`, { method: 'POST' });
            if (seedRes.ok) {
              const newSnap = await getDocs(collection(db, 'products'));
              productsData = newSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as Product[];
            } else {
               console.error('Seed API failed');
            }
          } catch (e) {
            console.error('Failed to trigger seed API:', e);
          }
        }

        setProducts(productsData);
        setStores(storesData);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col w-full min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="mb-12">
             <div className="w-48 h-10 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse mb-4" />
             <div className="w-72 h-5 bg-neutral-100 dark:bg-neutral-800/50 rounded animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex flex-col">
                <div className="aspect-[4/5] w-full bg-neutral-200 dark:bg-neutral-800 rounded-lg mb-4 animate-pulse" />
                <div className="w-full flex justify-between mb-2">
                  <div className="w-2/3 h-5 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
                  <div className="w-1/4 h-5 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
                </div>
                <div className="w-full h-4 bg-neutral-100 dark:bg-neutral-800/50 rounded animate-pulse mb-1" />
                <div className="w-4/5 h-4 bg-neutral-100 dark:bg-neutral-800/50 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const allCategories = Array.from(new Set(products.map(p => p.category).filter(Boolean) as string[])).sort();
  const allTags = Array.from(new Set(products.flatMap(p => p.tags || []))).sort();
  
  const filteredProducts = products.filter(p => {
    if (p.isArchived) return false;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory ? p.category === selectedCategory : true;
    const matchesTag = selectedTag ? p.tags?.includes(selectedTag) : true;
    return matchesSearch && matchesCategory && matchesTag;
  }).sort((a, b) => {
    if (sortOption === 'price_asc') {
      return a.price - b.price;
    } else if (sortOption === 'price_desc') {
      return b.price - a.price;
    }
    // "newest" basically whatever order, but assuming natural if no created_at
    return 0;
  });

  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://www.cardstonmarket.com/#homepage",
        "name": "Cardston Market — Shop Local Alberta Artisans & Handmade Goods Online",
        "description": "Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB.",
        "url": "https://www.cardstonmarket.com/",
        "isPartOf": { "@id": "https://www.cardstonmarket.com/#website" },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2"]
        }
      },
      {
        "@type": "ItemList",
        "@id": "https://www.cardstonmarket.com/#products",
        "name": "Cardston Market Products",
        "description": "Shop local goods from local vendors on Cardston Market",
        "url": "https://www.cardstonmarket.com",
        "numberOfItems": filteredProducts.length,
        "itemListElement": filteredProducts.slice(0, 20).map((product, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "url": `https://www.cardstonmarket.com/product/${product.id}`,
            "image": product.imageUrl,
            "offers": {
              "@type": "Offer",
              "price": (product.price / 100).toFixed(2),
              "priceCurrency": "CAD",
              "availability": product.stock !== undefined
                ? (product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock")
                : "https://schema.org/InStock",
              "seller": product.vendorId ? { "@id": `https://www.cardstonmarket.com/shop/${product.vendorId}#localbusiness` } : undefined
            }
          }
        }))
      },
      ...stores.slice(0, 10).map(store => ({
        "@type": "LocalBusiness",
        "@id": `https://www.cardstonmarket.com/shop/${store.uid}#localbusiness`,
        "name": store.storeName,
        "description": store.storeDescription || `Visit ${store.storeName} on Cardston Market`,
        "url": `https://www.cardstonmarket.com/shop/${store.uid}`,
        "address": store.city ? { "@type": "PostalAddress", "addressLocality": store.city, "addressRegion": "AB", "addressCountry": "CA" } : undefined,
        "image": store.profilePicture || store.bannerImage
      }))
    ]
  };

  return (
    <div className="flex flex-col w-full">
      <Helmet>
        <title>Cardston Market — Shop Local Alberta Artisans &amp; Handmade Goods Online</title>
        <meta name="description" content="Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB. Support local." />
        <link rel="canonical" href="https://www.cardstonmarket.com/" />
        <meta property="og:title" content="Cardston Market — Shop Local Alberta Artisans &amp; Handmade Goods Online" />
        <meta property="og:description" content="Shop handmade goods from local Alberta artisans on Cardston Market. Discover unique crafts, artisan foods & local products from Cardston, AB." />
        <meta property="og:url" content="https://www.cardstonmarket.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.cardstonmarket.com/og-banner.png" />
        <script type="application/ld+json">{JSON.stringify(homepageStructuredData)}</script>
      </Helmet>
      {showSuccess && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-6 py-4 rounded-2xl shadow-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-medium">Payment Successful!</p>
              {transactionId && <p className="text-sm opacity-80">Transaction ID: {transactionId}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="relative w-full h-[70vh] min-h-[500px] bg-neutral-900 flex items-center justify-center overflow-hidden"
      >
        <img 
          src="https://images.unsplash.com/photo-1556970256-05e8a6edd190?q=80&w=2000&auto=format&fit=crop" 
          alt="Cardston Market — Local artisan marketplace in Cardston, Alberta"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-neutral-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-neutral-900 via-transparent to-transparent" />
        
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto py-16">
          <h1 className="text-5xl md:text-7xl font-serif font-medium leading-tight mb-6 text-[#F5E6D3] drop-shadow-md">
              Cardston Market — Shop Local Alberta Artisans &amp; Handmade Goods Online
          </h1>
          <p className="text-lg md:text-xl font-medium max-w-xl mx-auto mb-10 text-[#E8DCC4] drop-shadow-md">
              Discover handcrafted goods and unique products from talented makers right here in our community.
          </p>
          <div className="flex flex-col items-center gap-6">
            <button 
              onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-4 rounded-full text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-lg"
            >
              Shop Collection <ArrowRight className="w-4 h-4" />
            </button>
            <Link 
              to="/sell" 
              className="group flex items-center gap-1.5 text-neutral-900 dark:text-neutral-300 hover:text-neutral-600 dark:hover:text-white transition-colors text-sm font-medium"
            >
              <Store className="w-4 h-4 stroke-[1.5]" />
              Are you a local maker? <span className="underline underline-offset-4 decoration-neutral-500 group-hover:decoration-neutral-900 dark:decoration-neutral-500 dark:group-hover:decoration-white">Start selling here</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full mt-[10px]">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">
              New Arrivals
            </h2>
            <p className="mt-2 text-neutral-500 dark:text-neutral-400">The latest additions to our collection.</p>
          </div>
          
          <div className="flex-1 max-w-md w-full">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-full focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white transition-colors text-sm bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
              />
            </div>
          </div>
          
          <div className="shrink-0 flex items-center">
            <select
               value={sortOption}
               onChange={(e) => setSortOption(e.target.value)}
               className="py-3 px-4 border border-neutral-200 dark:border-neutral-800 rounded-full text-sm bg-white dark:bg-neutral-900 focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white transition-colors text-neutral-900 dark:text-white appearance-none cursor-pointer"
               style={{ backgroundPosition: 'right 0.75rem center', paddingRight: '2.5rem', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundSize: '1em' }}
            >
               <option value="newest">Sort by: Newest</option>
               <option value="price_asc">Price: Low to High</option>
               <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {allCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center mb-6">
            <span className="text-sm font-medium text-neutral-500 mr-2">Categories:</span>
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === null 
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' 
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
            >
              All
            </button>
            {allCategories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category 
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' 
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center mb-10">
            <Tag className="w-4 h-4 text-neutral-400 mr-2" />
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedTag === null 
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' 
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedTag === tag 
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' 
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {filteredProducts.length === 0 ? (
          <div className="text-center py-24 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-100 dark:border-neutral-800">
            <ShoppingCart className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4 stroke-[1.5]" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white">No products found</h3>
            <p className="mt-1 text-neutral-500 dark:text-neutral-400">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12">
            {filteredProducts.map((product) => (
              <div key={product.id} className="group flex flex-col">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-lg mb-4">
                  <Link to="/product/$productId" params={{ productId: product.id }} className="absolute inset-0 z-0">
                    <img
                      src={product.imageUrl || 'https://picsum.photos/seed/product/400/500'}
                      alt={`Handmade ${product.name} by ${product.vendorName || 'Local Artisan'} on Cardston Market`}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 dark:group-hover:bg-white/5 transition-colors duration-300" />
                  </Link>
                  
                  <button
                    onClick={() => {
                      addItem({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        quantity: 1,
                        imageUrl: product.imageUrl,
                        description: product.description,
                        vendorId: product.vendorId,
                        vendorName: product.vendorName,
                        stock: product.stock,
                        maxOrderQuantity: product.maxOrderQuantity
                      });
                      toast.success(`${product.name} added to cart`);
                    }}
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 bg-white/90 dark:bg-neutral-900/90 backdrop-blur text-neutral-900 dark:text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-white dark:hover:bg-black shadow-sm flex items-center gap-2 w-[calc(100%-2rem)] justify-center"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Cart
                  </button>
                </div>
                
                <div className="flex flex-col flex-1">
                  <div className="flex justify-between items-start gap-4 mb-1">
                    <Link to="/product/$productId" params={{ productId: product.id }} className="text-base font-medium text-neutral-900 dark:text-white hover:underline">
                      {product.name}
                    </Link>
                    <p className="text-base font-medium text-neutral-900 dark:text-white">
                      ${(product.price / 100).toFixed(2)}
                    </p>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2 mt-1">
                    {product.description}
                  </p>
                  {product.vendorId && (
                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                      <Link 
                        to="/shop/$vendorId" 
                        params={{ vendorId: product.vendorId }}
                        className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                      >
                        <Store className="w-4 h-4" />
                        <span className="text-xs font-medium">{product.vendorName || 'Local Vendor'}</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Featured Vendors Section */}
      {stores.length > 0 && (
         <section className="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 border-t">
           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
             <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
               <div>
                  <h2 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight flex items-center gap-3">
                    <Store className="w-8 h-8 text-neutral-400 dark:text-neutral-500 stroke-[1.5]" />
                    Featured Local Stores
                  </h2>
                  <p className="mt-2 text-neutral-500 dark:text-neutral-400">Discover unique products from talented makers in your community.</p>
               </div>
             </div>
             
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {stores.slice(0, 4).map(store => (
                   <Link key={store.uid} to="/shop/$vendorId" params={{ vendorId: store.uid }} className="group block h-full">
                      <div className="bg-white dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-md hover:border-neutral-300 dark:hover:border-neutral-700">
                         <div className="h-24 relative overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                            {store.bannerType === 'image' && store.bannerImage ? (
                                <img src={store.bannerImage} alt={`${store.storeName} storefront banner on Cardston Market`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            ) : store.bannerType === 'gradient' && store.themeColors ? (
                               <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${store.themeColors.join(', ')})` }} />
                            ) : (
                               <div className="w-full h-full" style={{ backgroundColor: store.themeColors?.[0] || '#f3f4f6' }} />
                            )}
                         </div>
                         <div className="px-6 pb-6 pt-0 flex-1 flex flex-col items-center text-center relative">
                            <div className="w-16 h-16 rounded-full border-4 border-white dark:border-neutral-950 bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden -mt-8 mb-3 z-10 shadow-sm group-hover:scale-105 transition-transform relative">
                               {store.profilePicture ? (
                                   <img src={store.profilePicture} alt={`${store.storeName} vendor profile on Cardston Market`} className="w-full h-full object-cover" loading="lazy" />
                               ) : (
                                  <Store className="w-6 h-6 text-neutral-400 dark:text-neutral-500" />
                               )}
                            </div>
                            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-1 group-hover:underline">{store.storeName}</h3>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3 flex-shrink-0">{store.city || 'Local Area'}</p>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mt-auto font-light">
                               {store.storeDescription || 'Welcome to my shop!'}
                            </p>
                         </div>
                      </div>
                   </Link>
                ))}
             </div>
           </div>
         </section>
      )}
    </div>
  );
}
